import React, {memo, forwardRef, useCallback} from 'react';
import {useRouter} from '@/common/services/router';

type AnchorNativeProps = React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>;

export interface RouterLinkProps extends AnchorNativeProps {
	component?: React.ComponentType<AnchorNativeProps> | string;
	url: string;
}

// TODO POSSIBLE FEATURE: Onclick prevent url change, prefetch data (resolve route) and after define the new url

/**
 * An abstract link component that provides history writing instead of navigating with a page change
 * does not provide design and is not tied to any router implementation.
 * */
export const RouterLink = memo(
	forwardRef<HTMLElement, RouterLinkProps>((props, ref) => {
		const {component: Component = 'a', className, url, onClick, children, ...nativeProps} = props;

		const {history} = useRouter();

		const handleClick = useCallback(
			(event: React.MouseEvent<HTMLAnchorElement>) => {
				if (onClick) {
					onClick(event);
				}

				if (event.defaultPrevented) {
					return;
				}

				if (event.metaKey || event.shiftKey) {
					return;
				}

				const element = event.currentTarget;
				if (element.target === '_blank') {
					return;
				}

				if (history) {
					const {pathname, search, hash, hostname} = new URL(element.href);
					if (location.hostname !== hostname) {
						return;
					}

					event.preventDefault();
					history.push({pathname, search, hash});
				}
			},
			[onClick],
		);

		return (
			<Component ref={ref as any} className={className} href={url} onClick={handleClick} {...nativeProps}>
				{children}
			</Component>
		);
	}),
);

RouterLink.displayName = 'RouterLink';
